// src/pages/WeatherAlertResponse.jsx
// Focused landing page for weather-alert email/push CTAs. One alert, one
// decision. Coach (or manager) lands here from "Open Scrimly to Respond"
// and sees only the alert they need to act on — no dashboard noise.
//
// URL: /weather-alerts/:alertId
// Auth guard: the alert's coachIds list is the access credential. A logged-in
// user whose uid is NOT in coachIds is shown a permission-denied state.

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';

import {
  ArrowLeftIcon,
  ShieldExclamationIcon,
  CloudIcon,
} from '@heroicons/react/24/outline';

import WeatherAlertCard from '@/components/WeatherAlertCard';
import PageSpinner       from '@/components/PageSpinner';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

export default function WeatherAlertResponse() {
  const { alertId } = useParams();
  const { firebaseUser } = useAuth();
  const [alert, setAlert] = useState(undefined); // undefined = loading, null = doc missing
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!alertId) return;
    // Wait for auth to resolve so the snapshot read carries the user's
    // credentials — without this, onSnapshot may race and the rules engine
    // sees an anonymous request that falls through to a permission error.
    if (firebaseUser === undefined) return;
    const ref = doc(db, 'weatherAlerts', alertId);
    return onSnapshot(
      ref,
      (snap) => {
        setLoadError(null);
        setAlert(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      },
      (err) => {
        // Surface the error instead of silently collapsing into "not found" —
        // this was masking permission-denied and offline cases in production.
        // eslint-disable-next-line no-console
        console.error('[WeatherAlertResponse] load failed:', err);
        setLoadError(err);
        setAlert(null);
      },
    );
  }, [alertId, firebaseUser]);

  // --- Loading ---
  if (alert === undefined) return <PageSpinner />;

  // --- Load error (rules / network / auth timing) ---
  if (loadError) {
    return (
      <EmptyState
        icon={ShieldExclamationIcon}
        title="Couldn't load alert"
        body={loadError.code === 'permission-denied'
          ? "You don't have access to this alert. Sign in as the coach or manager on this team."
          : `Something went wrong (${loadError.code || 'unknown error'}). Please try again.`}
        tone="warn"
      />
    );
  }

  // --- Not found (doc missing) ---
  if (!alert) {
    return (
      <EmptyState
        title="Alert not found"
        body="This weather alert may have been resolved or removed."
      />
    );
  }

  // --- Permission check ---
  // coachIds contains every recipient (coach + manager + admin). Anyone else
  // with a link shouldn't be able to answer on behalf of the team.
  const uid = firebaseUser?.uid;
  if (uid && !alert.coachIds?.includes(uid)) {
    return (
      <EmptyState
        icon={ShieldExclamationIcon}
        title="You don't have access to this alert"
        body="Only the coaches and managers on this team can respond."
        tone="warn"
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12">
        <div className="mb-5">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-500 hover:text-[#5a8a1f] transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to home
          </Link>
        </div>

        <div className="mb-4">
          <p
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
            style={{ fontFamily: HEAD_FONT }}
          >
            Weather alert
          </p>
          <h1
            className="mt-1 text-2xl sm:text-3xl font-bold uppercase tracking-tight text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT }}
          >
            Weather check-in
          </h1>
          <p className="mt-2 text-sm text-zinc-600 leading-relaxed">
            Confirm or reschedule this event. Families will only get a
            cancellation text/email once you mark it cancelled or postponed.
          </p>
        </div>

        <WeatherAlertCard
          alert={alert}
          teamId={alert.teamId}
          dismissible={false}
        />
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon = CloudIcon, title, body, tone = 'neutral' }) {
  const iconBg = tone === 'warn'
    ? 'bg-amber-100 ring-1 ring-amber-200'
    : 'bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40';
  const iconColor = tone === 'warn' ? 'text-amber-700' : 'text-[#5a8a1f]';
  return (
    <div className="min-h-screen bg-[#f7faf5] flex items-start justify-center">
      <div className="max-w-md w-full mx-auto px-4 py-16 text-center">
        <div className={`mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl ${iconBg}`}>
          <Icon className={`h-7 w-7 ${iconColor}`} />
        </div>
        <h1
          className="text-xl font-bold uppercase text-[#0d1f14]"
          style={{ fontFamily: HEAD_FONT }}
        >
          {title}
        </h1>
        <p className="mt-3 text-sm text-zinc-600 leading-relaxed">{body}</p>
        <Link
          to="/dashboard"
          className="mt-6 inline-flex items-center gap-1.5 rounded-[10px] bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-colors"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          Go to home
        </Link>
      </div>
    </div>
  );
}
